import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sparkles, LogOut, User, ShieldAlert, LayoutDashboard, Menu, Bell, Trophy } from "lucide-react";
import { TIERS } from "@/lib/tiers";
import Avatar from "@/components/Avatar";
import RoleBadge from "@/components/RoleBadge";
import Username from "@/components/Username";
import NotificationBell from "@/components/NotificationBell";

export default function Header() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);

  const links = [
    { to: "/", label: "Home" },
    { to: "/posts", label: "TL Games" },
    { to: "/membership", label: "Membership" },
    ...(settings.supporter_hall_enabled !== false ? [{ to: "/supporters", label: "Supporters" }] : []),
    ...(settings.leaderboard_enabled !== false ? [{ to: "/leaderboard", label: "Leaderboard" }] : []),
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header className="sticky top-0 z-50 glass-strong border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <div className="relative w-9 h-9 rounded-md grid place-items-center bg-gradient-to-br from-rose-600 to-rose-900 pulse-glow">
            <span className="font-mystic text-xl text-white">鏡</span>
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold tracking-tight text-white text-base sm:text-lg">
              {settings.site_name}
            </div>
            <div className="font-mystic text-[11px] text-rose-300/80 -mt-0.5 hidden sm:block">
              黎明 — Crimson Dawn
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-md transition-colors ${
                  isActive ? "text-white bg-white/5" : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user && user !== false ? (
            <>
              <NotificationBell />
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 transition"
                  data-testid="user-menu-trigger"
                >
                  <Avatar user={user} size={28} />
                  <Username
                    user={{ ...user, name: user.name?.split(" ")[0] || user.name }}
                    className="hidden sm:inline text-sm"
                  />
                  <span className="hidden md:inline-flex">
                    <RoleBadge role={user.role} badges={user.badges} size="xs" />
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 glass-strong border-white/10" align="end">
                <DropdownMenuLabel className="text-zinc-400 text-xs">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/dashboard/profile")} data-testid="menu-profile">
                  <User className="w-4 h-4 mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/notifications")} data-testid="menu-notifications">
                  <Bell className="w-4 h-4 mr-2" /> Notifications
                </DropdownMenuItem>
                {settings.leaderboard_enabled !== false && (
                  <DropdownMenuItem onClick={() => nav("/leaderboard")} data-testid="menu-leaderboard">
                    <Trophy className="w-4 h-4 mr-2" /> Leaderboard
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                    <ShieldAlert className="w-4 h-4 mr-2 text-amber-400" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link">
                <Button variant="ghost" className="text-zinc-300 hover:text-white">Login</Button>
              </Link>
              <Link to="/register" data-testid="header-register-link">
                <Button className="bg-rose-600 hover:bg-rose-500 text-white font-semibold tracking-wide shadow-[0_0_20px_rgba(225,29,72,0.45)]">
                  <Sparkles className="w-4 h-4 mr-1.5" /> Join the Cult
                </Button>
              </Link>
            </>
          )}
          <button
            className="md:hidden p-2 rounded-md bg-white/5 border border-white/10"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/5 px-6 py-3 glass-strong">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block py-2 text-sm text-zinc-300 hover:text-white"
            >
              {l.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
